import { captureMessage } from '@sentry/browser';
import { kebabCase, noop } from 'lodash';
import { ReactElement, ReactNode, useCallback } from 'react';

import ActionsNode from 'dashboard/apps/InquiryTemplateEditor/ActionsNode';
import CompleteStepNode from 'dashboard/apps/InquiryTemplateEditor/CompleteStepNode';
import ConditionalNode from 'dashboard/apps/InquiryTemplateEditor/ConditionalNode';
import ConditionalRootNode from 'dashboard/apps/InquiryTemplateEditor/ConditionalRootNode';
import {
  CombinedRunStepForTree,
  CombinedStepForTree,
  LinkedWorkflowStep,
  TemplateStepPreview,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  getConditionalStepForToken,
  getNodeWrapperAttributes,
  isTreeStepWorkflowConditional,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import InquiryTemplateScreenPreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview';
import {
  calculateScreenFlowPadding,
  ConditionalWrapper,
  stepContentHeight,
  stepContentWidth,
  workflowErrorRouteDescription,
  workflowErrorRouteLabel,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import ScreenFlow from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow';
import {
  PathHighlightInformation,
  ScreenFlowConnector,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow/ScreenFlowElements';
import ScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper';
import ExpandableScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper/ExpandableScreenFlowNodeWrapper';
import { ScreenFlowNodeWrapperProps } from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper/ScreenFlowNodeWrapper';
import ScreenTransitionNode from 'dashboard/apps/InquiryTemplateEditor/ScreenTransitionNode';
import { uiStepAllComponents } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import WrappedConnectorNode from 'dashboard/apps/InquiryTemplateEditor/WrappedConnectorNode';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import {
  WorkflowStep,
  WorkflowStepConditional,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import { isTextComponent, isUiTransitionComponent } from 'lib/interfaces/ui-components';

type ConnectorStyles = {
  color?: string;
  strokeWidth?: number;
};

type BranchRootConnectorNodeReturnProps = Pick<
  ScreenFlowNodeWrapperProps,
  'title' | 'data-test' | 'node'
>;
interface InquiryTemplateVersionTreeBaseProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  workflowVersionMap: WorkflowVersionMap;
  tree: CombinedStepForTree[];
  getWorkflowHandleNodeClick?: (step: LinkedWorkflowStep) => (() => void) | undefined;
  getStepHandleNodeClick?: (step: TemplateStepPreview) => (() => void) | undefined;
  renderActionsNodeHighlighter?: (step: LinkedWorkflowStep) => ReactNode;
  renderConditionalRootNodeHighlighter?: (step: LinkedWorkflowStep) => ReactNode;
  renderWrappedConnectorNodeHighlighter?: (step: CombinedStepForTree) => ReactNode;
  renderCompleteStepNodeHighlighter?: (step: TemplateStepPreview) => ReactNode;
  renderScreenPreviewNodeHighlighter?: (step: TemplateStepPreview) => ReactNode;
  renderConditionalNodeHighlighter?: (
    step: LinkedWorkflowStep,
    workflowStep: WorkflowStepConditional,
    branchIndex: number,
  ) => ReactNode;
  renderUiStepScreenTransitionNodeHighlighter?: (
    step: TemplateStepPreview,
    branchIndex: number,
  ) => ReactNode;
  renderCombinedStepScreenTransitionNodeHighlighter?: (step: TemplateStepPreview) => ReactNode;
  getScreenFlowConnectorStyles?: (
    prevStep: CombinedStepForTree,
    nextStep: CombinedStepForTree,
  ) => ConnectorStyles | undefined;
  getBranchRootScreenFlowConnectorStyles?: (
    step: CombinedStepForTree,
    branchIndex: number,
  ) => ConnectorStyles | undefined;
  getPathHighlightInformationForStep?: (step: CombinedRunStepForTree) => PathHighlightInformation;
  getActionNodeWorkflowSteps?: (step: LinkedWorkflowStep) => WorkflowStep[];
  getConditionalNodeTooltipContents?: (step: LinkedWorkflowStep) => ReactNode | undefined;
}

/**
 * This base component is responsible for rendering a generic Inquiry Flow tree given some injected
 * dependencies. It is meant to be implemented for any particular variation of a flow tree.
 */
const InquiryTemplateVersionTreeBase = ({
  inquiryTemplateVersion,
  workflowVersionMap,
  tree,
  getWorkflowHandleNodeClick = () => noop,
  getStepHandleNodeClick = () => noop,
  renderActionsNodeHighlighter = noop,
  renderConditionalRootNodeHighlighter = noop,
  renderWrappedConnectorNodeHighlighter = noop,
  renderCompleteStepNodeHighlighter = noop,
  renderScreenPreviewNodeHighlighter = noop,
  renderConditionalNodeHighlighter = noop,
  renderUiStepScreenTransitionNodeHighlighter = noop,
  renderCombinedStepScreenTransitionNodeHighlighter = noop,
  getScreenFlowConnectorStyles,
  getBranchRootScreenFlowConnectorStyles,
  getPathHighlightInformationForStep,
  getActionNodeWorkflowSteps,
  getConditionalNodeTooltipContents,
}: InquiryTemplateVersionTreeBaseProps) => {
  const { theme } = inquiryTemplateVersion.attributes;

  const stepContentHeightWithPadding =
    stepContentHeight + calculateScreenFlowPadding(stepContentHeight);

  const renderCard = useCallback(
    (step: CombinedStepForTree) => {
      const { dataMarketingId, dataName, dataTest, title } = getNodeWrapperAttributes(step);
      let node: ReactNode = null;
      let expandNodes: ReactElement[] = [];
      let handleNodeClick = noop;
      if (step.type === 'workflow') {
        const linkedWorkflowStep: LinkedWorkflowStep = step;
        handleNodeClick = getWorkflowHandleNodeClick(linkedWorkflowStep) ?? handleNodeClick;

        switch (linkedWorkflowStep.workflowStep.type) {
          case WorkflowStepType.Action:
          case WorkflowStepType.Placeholder:
          case WorkflowStepType.Parallel: {
            node = (
              <ActionsNode
                title={title}
                workflowVersionId={linkedWorkflowStep.workflowVersionId}
                workflowStepToken={linkedWorkflowStep.workflowStep.token}
                onActionsDoubleClick={noop}
                workflowSteps={getActionNodeWorkflowSteps?.(linkedWorkflowStep)}
                nodeHighlighter={renderActionsNodeHighlighter(linkedWorkflowStep)}
              />
            );
            break;
          }
          case WorkflowStepType.Conditional: {
            node = (
              <ConditionalRootNode
                nodeHighlighter={renderConditionalRootNodeHighlighter(linkedWorkflowStep)}
                tooltipContents={getConditionalNodeTooltipContents?.(linkedWorkflowStep)}
              />
            );
            break;
          }
          default:
            return <></>;
        }
      } else if (step.isConnector) {
        return (
          <div>
            <WrappedConnectorNode
              connectingStep={step}
              dataName={dataName}
              dataTest={dataTest}
              dataMarketingId={dataMarketingId}
              handleConnectingStepChange={noop}
              steps={[]}
              title={title}
              theme={theme}
              nodeHighlighter={renderWrappedConnectorNodeHighlighter(step)}
            />
          </div>
        );
      } else {
        const templateStepPreview: TemplateStepPreview = step;
        switch (templateStepPreview.type) {
          case UiStepType.Complete: {
            node = (
              <CompleteStepNode
                nodeHighlighter={renderCompleteStepNodeHighlighter(templateStepPreview)}
              />
            );
            break;
          }
          case UiStepType.Document:
          case UiStepType.GovernmentId:
          case UiStepType.Integration:
          case UiStepType.Selfie:
          case UiStepType.Ui:
          case UiStepType.Loop: {
            handleNodeClick = getStepHandleNodeClick(templateStepPreview) ?? handleNodeClick;

            node = (
              <InquiryTemplateScreenPreview
                page={templateStepPreview.page}
                step={templateStepPreview.stepPreview}
                theme={theme}
                nodeHighlighter={renderScreenPreviewNodeHighlighter(templateStepPreview)}
              />
            );
            expandNodes = (templateStepPreview?.combinedStepPages ?? []).map((page) => (
              <InquiryTemplateScreenPreview
                key={page.key}
                page={page}
                step={templateStepPreview.stepPreview}
                theme={theme}
                nodeHighlighter={renderScreenPreviewNodeHighlighter(templateStepPreview)}
              />
            ));
            break;
          }
        }
      }

      const commonScreenWrapperProps = {
        node,
        title,
        'data-name': dataName,
        'data-test': dataTest,
        'data-marketing-id': dataMarketingId,
        editable: false,
        // don't show sticker for conditional workflow nodes
        withSticker: !isTreeStepWorkflowConditional(step),
        handleNodeClick,
      };

      return expandNodes.length > 0 ? (
        <ExpandableScreenFlowNodeWrapper {...commonScreenWrapperProps} expandNodes={expandNodes} />
      ) : (
        <ScreenFlowNodeWrapper {...commonScreenWrapperProps} />
      );
    },
    [theme],
  );

  const renderConnector = (
    prevStep: CombinedStepForTree,
    nextStep: CombinedStepForTree | undefined,
  ) =>
    nextStep != null ? (
      <ScreenFlowConnector
        height={stepContentHeightWithPadding}
        {...(getScreenFlowConnectorStyles?.(prevStep, nextStep) ?? {})}
      />
    ) : (
      <></>
    );

  const renderBranchRootConnector = (rootStep: CombinedStepForTree, branchIndex: number) => {
    const nextStep =
      rootStep.branches?.length && rootStep.branches[branchIndex]?.length
        ? rootStep.branches[branchIndex][0]
        : null;

    const branchRootConnectorRenderProps: BranchRootConnectorNodeReturnProps = {
      title: '',
      'data-test': '',
      node: <></>,
    };

    if (isTreeStepWorkflowConditional(rootStep)) {
      const linkedWorkflowRootStep: LinkedWorkflowStep = rootStep;

      const workflowStep = getConditionalStepForToken(
        linkedWorkflowRootStep.workflowStep.token,
        workflowVersionMap[linkedWorkflowRootStep.workflowVersionId],
      ) as WorkflowStepConditional; // Guaranteed to be found

      branchRootConnectorRenderProps.title =
        workflowStep.conditionals[branchIndex]?.name ?? `Route ${branchIndex + 1}`;
      branchRootConnectorRenderProps['data-test'] =
        `${workflowStep.token}__${workflowStep.type}-branch-${branchIndex}-version-diff-node`;

      branchRootConnectorRenderProps.node = (
        <ConditionalNode
          title={branchRootConnectorRenderProps.title}
          nodeHighlighter={renderConditionalNodeHighlighter(
            linkedWorkflowRootStep,
            workflowStep,
            branchIndex,
          )}
        />
      );
    } else {
      const templateStepPreviewRootStep: TemplateStepPreview = rootStep;

      branchRootConnectorRenderProps['data-test'] =
        `${templateStepPreviewRootStep.token}__${templateStepPreviewRootStep.type}-branch-${branchIndex}-screen-step-version-diff-node`;
      switch (templateStepPreviewRootStep.type) {
        case UiStepType.Document:
        case UiStepType.GovernmentId:
        case UiStepType.Selfie: {
          if (branchIndex === 0) {
            if (nextStep?.type === 'workflow') {
              branchRootConnectorRenderProps.title = nextStep.workflowStep.name;
            } else {
              // combined steps should only ever branch because workflow_version_id and workflow_on_error_step_name are set
              captureMessage(
                `Rendering flow tree branches from combined step: ${templateStepPreviewRootStep.name}, but nextStep is not type workflow`,
                'warning',
              );
              return <></>;
            }
          } else {
            branchRootConnectorRenderProps.title = workflowErrorRouteLabel;
          }
          const description =
            branchRootConnectorRenderProps.title === workflowErrorRouteLabel
              ? workflowErrorRouteDescription
              : null;
          branchRootConnectorRenderProps.node = (
            <ScreenTransitionNode
              title={branchRootConnectorRenderProps.title}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              description={description}
              nodeHighlighter={renderCombinedStepScreenTransitionNodeHighlighter(
                templateStepPreviewRootStep,
              )}
            />
          );
          break;
        }
        case UiStepType.Ui: {
          const transitionComponent = uiStepAllComponents(
            templateStepPreviewRootStep.stepPreview as StepUi,
          ).filter(isUiTransitionComponent)?.[branchIndex];
          const title = isTextComponent(transitionComponent)
            ? transitionComponent.attributes.text
            : `Route ${branchIndex + 1}`;
          branchRootConnectorRenderProps.node = (
            <ScreenTransitionNode
              title={title}
              nodeHighlighter={renderUiStepScreenTransitionNodeHighlighter(
                templateStepPreviewRootStep,
                branchIndex,
              )}
            />
          );
          break;
        }
        default:
          return <></>;
      }
    }

    return (
      <ConditionalWrapper>
        <ScreenFlowNodeWrapper
          {...branchRootConnectorRenderProps}
          data-name={kebabCase(branchRootConnectorRenderProps.title)}
          data-marketing-id='ite-version-diff-summary-tree-card'
          editable={false}
        />
        <ScreenFlowConnector
          height={stepContentHeightWithPadding}
          {...(getBranchRootScreenFlowConnectorStyles?.(rootStep, branchIndex) ?? {})}
        />
      </ConditionalWrapper>
    );
  };

  const { zoomableRef } = usePanZoomBoxControls();
  return (
    <ScreenFlow
      innerRef={zoomableRef}
      steps={tree}
      stepContentHeight={stepContentHeight}
      stepContentWidth={stepContentWidth}
      renderStep={renderCard}
      renderConnector={renderConnector}
      renderBranchRootConnector={renderBranchRootConnector}
      pathHighlightInformationForStep={getPathHighlightInformationForStep}
    />
  );
};

export default InquiryTemplateVersionTreeBase;
